import React, { useState } from 'react';
import { Search, UserPlus, Loader2 } from 'lucide-react';
import { translations } from '../../translations';

interface SearchBarProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onAdd: (username: string) => Promise<void>;
  isLoading: boolean;
  isDarkMode: boolean;
  language: string;
  onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export function SearchBar({ 
  searchQuery, 
  onSearchChange, 
  onAdd, 
  isLoading, 
  isDarkMode, 
  language,
  onKeyPress 
}: SearchBarProps) {
  const t = translations[language as keyof typeof translations];
  const [bulkTargets, setBulkTargets] = useState('');

  const handleBulkAdd = async () => {
    if (!bulkTargets.trim() || isLoading) return;

    const targets = bulkTargets
      .split(',')
      .map(target => target.trim())
      .filter(target => target.length > 0);

    for (const target of targets) {
      await onAdd(target);
    }

    setBulkTargets('');
  };

  return (
    <div className="space-y-4">
      <div className="relative">
        <input
          type="text"
          placeholder={t.search_targets}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          onKeyPress={onKeyPress}
          maxLength={31}
          className={`w-full rounded-lg pl-9 ${searchQuery ? 'pr-[100px]' : 'pr-4'} py-2 text-[13px] placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-orange-500 ${
            isDarkMode 
              ? 'bg-[#2b2b2b] text-gray-200' 
              : 'bg-gray-50 text-gray-900'
          }`}
          disabled={isLoading}
        />
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" strokeWidth={2.3} />
        {searchQuery && (
          <button
            onClick={() => !isLoading && onAdd(searchQuery)}
            disabled={isLoading}
            className={`absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 rounded-md transition-colors flex items-center gap-1.5 ${
              isDarkMode
                ? 'bg-[#3b3b3b] text-gray-200 hover:bg-[#4b4b4b]'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isLoading ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                <span className="text-[13px]">{t.verifying}</span>
              </>
            ) : (
              <>
                <UserPlus size={14} strokeWidth={2.3} />
                <span className="text-[13px]">{t.add}</span>
              </>
            )}
          </button>
        )}
      </div>

      <div className="relative">
        <textarea
          placeholder={t.add_multiple_targets}
          value={bulkTargets}
          onChange={(e) => setBulkTargets(e.target.value)}
          rows={3}
          className={`w-full rounded-lg px-4 py-2 text-[13px] placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-orange-500 resize-none ${
            isDarkMode 
              ? 'bg-[#2b2b2b] text-gray-200' 
              : 'bg-gray-50 text-gray-900'
          }`}
          disabled={isLoading}
        />
        {bulkTargets.trim() && (
          <button
            onClick={handleBulkAdd}
            disabled={isLoading}
            className={`absolute bottom-2 right-2 px-3 py-1 rounded-md transition-colors flex items-center gap-1.5 ${
              isDarkMode
                ? 'bg-[#3b3b3b] text-gray-200 hover:bg-[#4b4b4b]'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isLoading ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                <span className="text-[13px]">{t.verifying}</span>
              </>
            ) : (
              <>
                <UserPlus size={14} strokeWidth={2.3} />
                <span className="text-[13px]">{t.add}</span>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}